import {ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ExtendedAnhang, Nachricht, NachrichtSummary} from '../core/model/nachricht/nachricht';
import {getOrdnerEnum, OrdnerEnum} from '../core/model/ordner.enum';
import {HeaderComponent} from '../core/components/header/header.component';
import {PagesEnum} from '../core/model/pages.enum';
import {getPostfachFormat} from '../shared/utils/date-time.helper';
import {Subject, takeUntil} from 'rxjs';
import {AnhangDownloadService} from '../core/services/apiservices/anhang-download.service';
import {DownloadHelperService} from '../core/services/download-helper.service';
import {NachrichtAction} from '../shared/components/nachricht-action-buttons/nachricht-action.enum';
import {NachrichtActionButtonProvider} from '../core/services/nachricht-action-button.provider';
import {
    NachrichtActionModalsComponent
} from '../shared/components/nachricht-action-modals/nachricht-action-modals.component';
import {NavigationService} from '../shared/utils/navigation.service';
import {NachrichtHistoryComponent} from './nachricht-history/nachricht-history.component';
import {FilesizeTransformerService} from '../core/services/filesize.transformer.service';
import {FooterComponent} from '../shared/components/footer/footer.component';
import {FooterButton, FooterButtonType} from '../shared/components/footer/footer-button';
import i18next from 'i18next';
import {I18NextPipe} from '@kokos-i18next';
import {SanitizeService} from '../shared/utils/sanitize.service';
import {LinkifyService} from '../shared/utils/linkify.service';
import {NachrichtDetailComponent} from '../shared/components/nachricht-detail/nachricht-detail.component';
import {UserAccountService} from '../core/services/user-account.service';
import {PersonDisplayService} from "../core/services/person-display.service";
import {I18nRerenderOnLanguageChangeDirective} from "../shared/directives/i18n-rerender-on-language-change.directive";

@Component({
    selector: 'kokos-nachricht-anzeigen',
    imports: [
        HeaderComponent,
        NachrichtActionModalsComponent,
        NachrichtHistoryComponent,
        NachrichtDetailComponent,
        FooterComponent,
        I18NextPipe,
        I18nRerenderOnLanguageChangeDirective,
    ],
    providers: [NachrichtActionButtonProvider],
    templateUrl: './nachricht-anzeigen.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class NachrichtAnzeigenComponent implements OnInit, OnDestroy {
    @ViewChild('nachrichtActionModals', {static: false})
    nachrichtActionModalsComponent!: NachrichtActionModalsComponent;

    nachricht!: Nachricht;

    page = PagesEnum.ReadMail;

    protected readonly getPostfachFormat = getPostfachFormat;

    private readonly route = inject(ActivatedRoute);
    private readonly anhangDownloadService = inject(AnhangDownloadService);
    private readonly downloadHelperService = inject(DownloadHelperService);
    private readonly userAccountService = inject(UserAccountService);
    private readonly nachrichtActionButtonProvider = inject(NachrichtActionButtonProvider);
    private readonly nav = inject(NavigationService);
    private readonly fileSizeTransformerService = inject(FilesizeTransformerService);
    private readonly sanitizeService = inject(SanitizeService);
    private readonly linkifyService = inject(LinkifyService);
    protected readonly personDisplayService = inject(PersonDisplayService);

    private readonly destroy$ = new Subject<void>();

    postfachRestricted = false;
    sanitizedText = '';

    ngOnInit(): void {
        this.route.data
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: any) => {
                this.nachricht = {...data.nachricht, gelesen: true};
                const sanitized = this.sanitizeService.sanitize(this.nachricht.text?.replaceAll('\n', '<br/>'));
                this.sanitizedText = this.linkifyService.linkify(sanitized);
            });

        this.postfachRestricted = this.userAccountService.isRestricted();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    handleBackClicked(): void {
        this.nav.goTo('ordner/' + getOrdnerEnum(this.nachricht.ordnerType));
    }

    downloadAnhang(position: number): void {
        const anhang = this.nachricht.anhaengeMetadaten[position];
        this.anhangDownloadService
            .fetchDownload(this.nachricht.ordnerType, this.nachricht.nachrichtId, anhang.anhangId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (blob) => {
                    this.downloadHelperService.downloadBlob(blob, anhang.dateiname);
                },
                error: (err) => {
                    throw err;
                },
            });
    }

    getNachrichtActions(): NachrichtAction[] {
        return this.nachrichtActionButtonProvider.getButtons(
            getOrdnerEnum(this.nachricht.ordnerType),
            this.nachricht,
            !this.postfachRestricted,
        );
    }

    onNachrichtActionButtonClicked(action: NachrichtAction, nachricht: NachrichtSummary): void {
        this.nachrichtActionModalsComponent.handleAction(action, nachricht);
    }

    onReload(ordner: OrdnerEnum): void {
        this.nav.goTo(['/ordner', ordner]);
    }

    getAnhaenge(): ExtendedAnhang[] {
        const list = this.nachricht?.anhaengeMetadaten ?? [];
        return list.map((anhang) => {
            const displayGroesse = this.fileSizeTransformerService.transform(anhang.groesse, 0);
            return {...anhang, displayGroesse};
        });
    }

    get backButtonConfig(): FooterButton {
        return {label: i18next.t('navigation.back'), type: FooterButtonType.back};
    }
}